import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle, Circle, ChevronRight, ChevronLeft,
  Package, Building2, FileText, FileCheck, Sparkles,
  ArrowRight, Loader2, Mail, AlertCircle, ExternalLink, Check
} from "lucide-react";

// ─── Config ───────────────────────────────────────────────────────────────────
// Replace these with your real SignNow template IDs from app.signnow.com/webapp/template/{ID}
const SIGNNOW_TEMPLATES = {
  contract: "4256092e27d540cb8dd7ad12db30fdf35b457086",
  nda:      "705981fa8058452485b5e02281de792d395d8de2",
  w9:       "8c4e386f80ea46cd8b22c9f61208e3ad98266a01",
};

// ─── Step data ────────────────────────────────────────────────────────────────
const STEPS = [
  { key: "welcome",   label: "Welcome",   icon: Sparkles },
  { key: "company",   label: "Company",   icon: Building2 },
  { key: "contract",  label: "Contract",  icon: FileText },
  { key: "nda",       label: "NDA",       icon: FileText },
  { key: "tax",       label: "W-9 Form",  icon: FileCheck },
  { key: "products",  label: "Products",  icon: Package },
  { key: "complete",  label: "Done!",     icon: CheckCircle },
];

const PRODUCTS = [
  {
    name: "SalesCRM Core",
    tagline: "The foundation of your pipeline",
    description: "Full CRM suite with lead management, deal tracking, and automated follow-ups. Built for speed.",
    price: "$49/mo per seat",
    highlights: ["Unlimited leads", "Visual pipeline", "Email sequences", "Mobile app"],
    color: "#6366f1",
  },
  {
    name: "AI Sales Agent",
    tagline: "Your 24/7 closer",
    description: "An AI-powered assistant that qualifies leads, drafts outreach, and suggests next steps in real time.",
    price: "$99/mo per seat",
    highlights: ["Lead scoring", "Auto-drafts", "Call summaries", "Objection handling"],
    color: "#0ea5e9",
  },
  {
    name: "Analytics Pro",
    tagline: "See the whole picture",
    description: "Deep reporting across your entire team. Forecast revenue, track conversion rates, and spot trends.",
    price: "$29/mo per seat",
    highlights: ["Revenue forecast", "Team leaderboards", "Custom dashboards", "CSV exports"],
    color: "#10b981",
  },
];

// ─── Mock user ────────────────────────────────────────────────────────────────
const MOCK_USER = {
  full_name: "Test User",
  email: "testuser@example.com",
  role: "contractor",
  onboarding: { contract_signed: false, nda_signed: false, tax_form_signed: false },
};

// ─── SignNow Embedded Signing Step ────────────────────────────────────────────
function SignNowStep({ docKey, docLabel, templateId, signerEmail, signerName, onSigned, signed }) {
  const [state, setState] = useState("idle"); // idle | loading | ready | polling | error
  const [signingUrl, setSigningUrl] = useState(null);
  const [documentId, setDocumentId] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  async function initSigning() {
    setState("loading");
    setErrorMsg(null);
    try {
      const res = await fetch("/api/signnow/invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          templateId,
          signerEmail,
          signerName,
          documentName: `${docLabel} — ${signerName}`,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(JSON.stringify(data));
      if (!data.signingLink) throw new Error("No signing link returned from SignNow.");

      setSigningUrl(data.signingLink);
      setDocumentId(data.documentId);
      setState("ready");
      startPolling(data.documentId);
    } catch (err) {
      setErrorMsg(err.message);
      setState("error");
    }
  }

  function startPolling(docId) {
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/signnow/status?documentId=${docId}`);
        const data = await res.json();
        if (data.signed) {
          clearInterval(pollRef.current);
          onSigned(docKey);
        }
      } catch {
        // Silently continue polling on transient errors
      }
    }, 5000);
  }

  // Already signed — show confirmation using existing .settings-doc-row pattern
  if (signed) {
    return (
      <div className="card-body">
        <h2 className="page-title" style={{ fontSize: 18, marginBottom: 16 }}>{docLabel}</h2>
        <div className="settings-doc-row">
          <CheckCircle size={18} color="#10b981" />
          <div>
            <div className="settings-doc-label">{docLabel} signed successfully</div>
            <div className="settings-doc-status signed">A copy has been emailed to {signerEmail}.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-body">
      <h2 className="page-title" style={{ fontSize: 18, marginBottom: 8 }}>{docLabel}</h2>

      {/* Idle — prompt to begin */}
      {state === "idle" && (
        <>
          <p className="page-subtitle" style={{ marginBottom: 16 }}>
            Please review and sign your {docLabel}. We'll send a copy to{" "}
            <strong>{signerEmail}</strong> automatically once complete.
          </p>

          {/* "How it works" info box — reuses .agent-banner style */}
          <div className="agent-banner" style={{ marginBottom: 16 }}>
            <Mail size={18} color="#2563eb" style={{ flexShrink: 0 }} />
            <div className="agent-banner-text">
              <strong>How it works</strong>
              <span>
                The document opens in a secure signing window below. After you sign, a countersigned
                copy goes to your email automatically. No downloads or printing required.
              </span>
            </div>
          </div>

          <button className="btn btn-primary" onClick={initSigning}>
            Open {docLabel} to Sign <ArrowRight size={14} />
          </button>
        </>
      )}

      {/* Loading */}
      {state === "loading" && (
        <div className="placeholder-box">
          <Loader2 size={28} color="#2563eb" style={{ animation: "spin 1s linear infinite" }} />
          <p className="placeholder-desc">Preparing your document…</p>
        </div>
      )}

      {/* Error — reuses .settings-error */}
      {state === "error" && (
        <div style={{ marginBottom: 16 }}>
          <div className="settings-error">
            <AlertCircle size={16} />
            <span>{errorMsg}</span>
          </div>
          <button className="btn btn-secondary" onClick={initSigning}>Retry</button>
        </div>
      )}

      {/* Signing iframe */}
      {state === "ready" && signingUrl && (
        <>
          <div className="card" style={{ marginBottom: 12, overflow: "hidden" }}>
            <iframe
              src={signingUrl}
              title={`Sign: ${docLabel}`}
              width="100%"
              height="680"
              style={{ display: "block", border: "none" }}
              allow="camera; microphone"
            />
          </div>

          {/* Fallback link */}
          <p style={{ fontSize: 12, color: "#475569", marginBottom: 12 }}>
            Document not loading?{" "}
            <a
              href={signingUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#2563eb", textDecoration: "underline" }}
            >
              Open in a new tab <ExternalLink size={11} style={{ display: "inline", verticalAlign: "middle" }} />
            </a>
          </p>

          {/* Manual confirmation fallback — reuses .agent-banner */}
          <div className="agent-banner">
            <FileCheck size={18} color="#2563eb" style={{ flexShrink: 0 }} />
            <div className="agent-banner-text">
              <strong>Signed in a new tab?</strong>
              <span>Confirm here to continue.</span>
            </div>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                clearInterval(pollRef.current);
                onSigned(docKey);
              }}
            >
              I've completed my signature
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Step components ──────────────────────────────────────────────────────────

function WelcomeStep({ user }) {
  return (
    <div className="card-body">
      {/* Welcome icon — reuses .placeholder-icon */}
      <div className="placeholder-box" style={{ border: "none", paddingTop: 0 }}>
        <div className="placeholder-icon blue">
          <Sparkles size={28} color="#2563eb" />
        </div>
        <h2 className="placeholder-title" style={{ fontSize: 20 }}>
          Welcome to the team, {user?.full_name?.split(" ")[0] ?? "there"}! 👋
        </h2>
        <p className="placeholder-desc">
          We're excited to have you on board. This quick walkthrough will get you set up in about
          5 minutes — covering the company, our products, and a couple of documents to sign.
        </p>
      </div>

      {/* Checklist — reuses .settings-doc-row */}
      <div className="settings-docs-list" style={{ marginTop: 16 }}>
        {STEPS.slice(1, -1).map(s => (
          <div key={s.key} className="settings-doc-row">
            <Circle size={16} color="#94a3b8" />
            <div className="settings-doc-label">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompanyStep() {
  return (
    <div className="card-body">
      <h2 className="page-title" style={{ fontSize: 18, marginBottom: 4 }}>About Propertunity</h2>
      <p className="page-subtitle" style={{ marginBottom: 20 }}>
        Private lending meets real estate brokerage — built for builders and investors who move fast.
      </p>

      {/* Origin story — reuses .agent-banner as a highlight block */}
      <div className="agent-banner" style={{ marginBottom: 20 }}>
        <div className="agent-banner-text">
          <strong>Why We Built This</strong>
          <span>
            We started as a private lending firm and kept running into the same wall — our clients
            could get funded, but selling finished properties was always the bottleneck. So we did
            something about it. Propertunity funds the deal. Lem Lux Realty sells it. One ecosystem,
            zero gaps.
          </span>
        </div>
      </div>

      {/* Core facts — reuses .stats-grid + .stat-card */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {[
          {
            title: "What We Do",
            body: "Private lending firm specializing in bridge and term loans for residential investment projects — fix-and-flip, ground-up construction, and buy-to-rent.",
            cls: "blue",
          },
          {
            title: "Lem Lux Realty",
            body: "Our in-house brokerage spun off to solve one problem: moving finished inventory faster. Builders get a dedicated sales team. Agents get real, ready-to-sell listings.",
            cls: "purple",
          },
          {
            title: "Your Role",
            body: "You're an independent contractor rep. You'll be connecting investors and builders with our loan products and, where relevant, our brokerage services.",
            cls: "green",
          },
          {
            title: "Commission",
            body: "20% recurring on closed loan originations. Paid monthly, net-15. Brokerage referral bonuses apply separately.",
            cls: "orange",
          },
        ].map(c => (
          <div key={c.title} className="stat-card" style={{ alignItems: "flex-start", flexDirection: "column", gap: 6 }}>
            <div className="stat-label" style={{ fontWeight: 600, color: "#0f172a" }}>{c.title}</div>
            <p style={{ fontSize: 13, color: "#475569", margin: 0, lineHeight: 1.5 }}>{c.body}</p>
          </div>
        ))}
      </div>

      {/* Who wins — reuses .settings-docs-list + .settings-doc-row */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-title">The Ecosystem — Who Wins</span>
        </div>
        <div className="settings-docs-list" style={{ padding: "0 20px" }}>
          {[
            { who: "Builders",   win: "A sales team ready to move their projects from day one.", cls: "blue" },
            { who: "Investors",  win: "Faster capital recycling — sell quicker, fund the next deal sooner.", cls: "green" },
            { who: "Agents",     win: "Real, ready-to-sell inventory without chasing cold leads.", cls: "purple" },
          ].map(r => (
            <div key={r.who} className="settings-doc-row">
              <span className={`badge badge-${r.cls}`} style={{ minWidth: 72, justifyContent: "center" }}>{r.who}</span>
              <div className="settings-doc-label" style={{ fontWeight: 400, color: "#475569" }}>{r.win}</div>
            </div>
          ))}
        </div>
      </div>

      {/* First-week tips — reuses .agent-banner */}
      <div className="agent-banner" style={{ flexDirection: "column", alignItems: "flex-start" }}>
        <div className="agent-banner-text">
          <strong>Quick tips for your first week</strong>
        </div>
        <ul style={{ margin: "8px 0 0 18px", display: "flex", flexDirection: "column", gap: 6 }}>
          <li style={{ fontSize: 13, color: "#3b82f6" }}>Reach out to your manager on Slack — they'll walk you through active loan scenarios</li>
          <li style={{ fontSize: 13, color: "#3b82f6" }}>Review our bridge vs. term loan criteria so you can qualify leads fast</li>
          <li style={{ fontSize: 13, color: "#3b82f6" }}>Set your calendar for discovery calls — most closes start with a 20-minute intro</li>
        </ul>
      </div>
    </div>
  );
}

function ProductsStep() {
  const [selected, setSelected] = useState(null);
  return (
    <div className="card-body">
      <h2 className="page-title" style={{ fontSize: 18, marginBottom: 4 }}>Our Products</h2>
      <p className="page-subtitle" style={{ marginBottom: 20 }}>You'll be selling these three products. Click each to learn more.</p>

      {/* Products — reuses .pipeline-card pattern */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {PRODUCTS.map((p, i) => (
          <div
            key={p.name}
            className="pipeline-card"
            style={{
              borderLeft: `3px solid ${p.color}`,
              background: selected === i ? "#f8fafc" : "#fff",
            }}
            onClick={() => setSelected(selected === i ? null : i)}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 10, height: 10, borderRadius: "50%",
                  background: p.color, flexShrink: 0,
                }} />
                <div>
                  <div className="pipeline-card-name">{p.name}</div>
                  <div className="pipeline-card-sub">{p.tagline}</div>
                </div>
              </div>
              <span className="badge badge-outline">{p.price}</span>
            </div>

            {selected === i && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
                <p style={{ fontSize: 13, color: "#475569", marginBottom: 10 }}>{p.description}</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {p.highlights.map(h => (
                    <span key={h} className="badge badge-outline" style={{ borderColor: p.color, color: p.color, gap: 4 }}>
                      <Check size={11} color={p.color} />{h}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CompleteStep() {
  const navigate = useNavigate();
  return (
    <div className="card-body">
      <div className="placeholder-box" style={{ border: "none" }}>
        <div className="placeholder-icon green">
          <CheckCircle size={32} color="#16a34a" />
        </div>
        <h2 className="placeholder-title" style={{ fontSize: 20 }}>You're all set! 🎉</h2>
        <p className="placeholder-desc">
          Your onboarding is complete. Head to your dashboard to start working your pipeline.
        </p>
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button className="btn btn-primary" onClick={() => navigate("/")}>
            Go to Dashboard <ArrowRight size={14} />
          </button>
          <button className="btn btn-secondary" onClick={() => navigate("/leads")}>
            View My Leads
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Shell ───────────────────────────────────────────────────────────────

export default function OnboardingTest() {
  const [step, setStep] = useState(0);
  const [signed, setSigned] = useState({
    contract: MOCK_USER.onboarding.contract_signed,
    nda:      MOCK_USER.onboarding.nda_signed,
    tax:      MOCK_USER.onboarding.tax_form_signed,
  });

  function handleSigned(docKey) {
    setSigned(s => ({ ...s, [docKey]: true }));
  }

  const stepSignatureRequirement = { 2: "contract", 3: "nda", 4: "tax" };

  function canAdvance() {
    const req = stepSignatureRequirement[step];
    if (req && !signed[req]) return false;
    return true;
  }

  const lastStep = STEPS.length - 1;

  return (
    <>
      {/* Dev mode banner */}
      <div style={{
        background: "#854d0e", color: "#fef9c3", fontSize: 12, fontWeight: 600,
        textAlign: "center", padding: "6px 16px", letterSpacing: "0.05em",
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
      }}>
        ⚠ TEST MODE — Using mock user · Replace SIGNNOW_TEMPLATES IDs before production
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/*
        Outer shell — reuses .crm-shell layout so the sidebar + main area
        mirror the rest of the app exactly.
      */}
      <div className="crm-shell" style={{ paddingTop: 32 }}>

        {/* Sidebar progress — reuses .crm-sidebar */}
        <aside className="crm-sidebar">
          <div className="crm-sidebar-logo">
            <div className="crm-sidebar-logo-icon">
              <img src="/Logo.png" alt="Logo" style={{ width: 20, height: 20 }} />
            </div>
            <span>SalesCRM</span>
          </div>

          <nav className="crm-nav" style={{ paddingTop: 20 }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: "#64748b", padding: "0 12px 8px", letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Getting started
            </p>

            {STEPS.map((s, i) => {
              const done   = i < step;
              const active = i === step;
              const Icon   = s.icon;
              return (
                <div
                  key={s.key}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 12px", borderRadius: 8,
                    marginBottom: 2,
                    background: active ? "#2563eb" : done ? "#1e293b" : "transparent",
                    color: active ? "#fff" : done ? "#94a3b8" : "#64748b",
                    fontSize: 13.5, fontWeight: 500,
                    position: "relative",
                  }}
                >
                  {done
                    ? <Check size={14} color="#10b981" />
                    : <Icon size={14} color={active ? "#fff" : "#64748b"} />
                  }
                  <span>{s.label}</span>
                  {done && (
                    <span className="badge badge-green" style={{ marginLeft: "auto", fontSize: 10, padding: "1px 6px" }}>
                      Done
                    </span>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Dev jump buttons */}
          <div style={{ padding: "16px 14px", borderTop: "1px solid #1e293b" }}>
            <p style={{ fontSize: 11, color: "#64748b", marginBottom: 8 }}>Jump to step</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {STEPS.map((s, i) => (
                <button
                  key={s.key}
                  onClick={() => setStep(i)}
                  className="btn btn-sm"
                  style={{
                    fontSize: 10, padding: "2px 7px",
                    background: step === i ? "#2563eb" : "#1e293b",
                    color: step === i ? "#fff" : "#94a3b8",
                    border: "1px solid #334155",
                  }}
                >
                  {i}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Main content — reuses .crm-main + .crm-content */}
        <div className="crm-main">
          <div className="crm-content">
            <div className="page">

              {/* Step card — reuses .card */}
              <div className="card" style={{ maxWidth: 860, margin: "0 auto" }}>

                {step === 0 && <WelcomeStep user={MOCK_USER} />}
                {step === 1 && <CompanyStep />}

                {step === 2 && (
                  <SignNowStep
                    docKey="contract"
                    docLabel="Independent Contractor Agreement"
                    templateId={SIGNNOW_TEMPLATES.contract}
                    signerEmail={MOCK_USER.email}
                    signerName={MOCK_USER.full_name}
                    onSigned={handleSigned}
                    signed={signed.contract}
                  />
                )}

                {step === 3 && (
                  <SignNowStep
                    docKey="nda"
                    docLabel="Non-Disclosure Agreement"
                    templateId={SIGNNOW_TEMPLATES.nda}
                    signerEmail={MOCK_USER.email}
                    signerName={MOCK_USER.full_name}
                    onSigned={handleSigned}
                    signed={signed.nda}
                  />
                )}

                {step === 4 && (
                  <SignNowStep
                    docKey="tax"
                    docLabel="W-9 Tax Form"
                    templateId={SIGNNOW_TEMPLATES.w9}
                    signerEmail={MOCK_USER.email}
                    signerName={MOCK_USER.full_name}
                    onSigned={handleSigned}
                    signed={signed.tax}
                  />
                )}

                {step === 5 && <ProductsStep />}
                {step === 6 && <CompleteStep />}

                {/* Navigation footer — reuses .drawer-footer pattern */}
                {step < lastStep && (
                  <div className="drawer-footer" style={{ justifyContent: "space-between" }}>
                    {step > 0 ? (
                      <button className="btn btn-secondary" onClick={() => setStep(s => s - 1)}>
                        <ChevronLeft size={15} /> Back
                      </button>
                    ) : (
                      <span />
                    )}
                    <button
                      className="btn btn-primary"
                      disabled={!canAdvance()}
                      onClick={() => setStep(s => s + 1)}
                    >
                      {step === lastStep - 1 ? "Finish" : "Continue"}
                      <ChevronRight size={15} />
                    </button>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
}